import { FC, useState } from 'react';
import { Stack } from '@mui/material';
import {
  AutocompleteInput,
  Modal,
  SwitchInput,
  TextField,
} from 'melp-design/components';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import { useTranslation } from 'react-i18next';
import { ModalProps } from 'store/modal';
import {
  DepartmentManager,
  useCreateDepartment,
  useUpdateDepartment,
} from 'store/departments';
import { useEmployees } from 'store/employees';
import { usePredefinedToasts } from 'utils/Toast';
import { useDebouncedCallback } from 'use-debounce';

interface Values {
  name: string;
  visible: boolean;
  managerId: string;
}

const INITIAL_VALUES: Values = {
  name: '',
  visible: false,
  managerId: '',
};

interface Props extends ModalProps {
  companyId: string;
  departmentId?: string;
  initialValues?: Values;
  manager?: DepartmentManager | null;
}

export const CompanyDepartmentModal: FC<Props> = ({
  closeModal,
  companyId,
  departmentId,
  initialValues,
  manager,
}) => {
  const { t } = useTranslation();
  const predefinedToasts = usePredefinedToasts();

  const [employeeSearch, setEmployeeSearch] = useState('');
  const handleEmployeeSearch = useDebouncedCallback(setEmployeeSearch, 300);

  const { control, handleSubmit } = useForm<Values>({
    defaultValues: {
      ...(initialValues ?? INITIAL_VALUES),
    },
  });

  const { data: employees } = useEmployees({
    search: employeeSearch,
  });

  const { mutate: createDepartment, isLoading: isCreating } =
    useCreateDepartment();
  const { mutate: updateDepartment, isLoading: isUpdating } =
    useUpdateDepartment();

  const onSubmit: SubmitHandler<Values> = (values) => {
    const data = {
      name: values.name,
      hidden: !values.visible,
      managerId: values.managerId || null,
    };

    if (departmentId) {
      updateDepartment(
        { departmentId, data },
        {
          onSuccess: () => {
            predefinedToasts.success.updated();
            closeModal();
          },
        },
      );
    } else {
      createDepartment(
        { companyId, ...data },
        {
          onSuccess: () => {
            predefinedToasts.success.added();
            closeModal();
          },
        },
      );
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Content
        title={departmentId ? t('common.edit') : t('companies.add_department')}
        actions={[
          {
            variant: 'neutral-outline',
            onClick: () => closeModal(),
            label: t('common.cancel'),
          },
          {
            variant: 'primary',
            label: t('common.save'),
            type: 'submit',
            disabled: isCreating || isUpdating,
          },
        ]}
      >
        <Stack spacing={2}>
          <Controller
            name="name"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <TextField
                label={t('companies.department_name')}
                name={name}
                value={value}
                onChange={onChange}
                required
                ref={ref}
              />
            )}
          />

          <Controller
            name="managerId"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <AutocompleteInput
                label={t('companies.manager.label')}
                name={name}
                value={value}
                onChange={onChange}
                onInputChange={handleEmployeeSearch}
                options={
                  employees?.items.map((employee) => ({
                    label: employee.fullName,
                    value: employee.id,
                    caption: t('marketplace.employeeID', {
                      id: employee.id,
                    }),
                  })) ?? []
                }
                preselectedOptions={
                  manager
                    ? [{ value: manager.id, label: manager.name }]
                    : undefined
                }
                ref={ref}
              />
            )}
          />

          <Controller
            name="visible"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <SwitchInput
                label={t('departments.visible.label')}
                name={name}
                value={value}
                onChange={onChange}
                ref={ref}
              />
            )}
          />
        </Stack>
      </Modal.Content>
    </form>
  );
};
